import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { RSAEncryptionService } from '@app/core/services/rsa-encryption.service';

export const rsaEncryptionInterceptor: HttpInterceptorFn = (req, next) => {
    console.log('RSA interceptor triggered for URL:', req.url);

    const rsaEncryptionService = inject(RSAEncryptionService);

    const sensitiveFields = ['cci', 'documentNumber', 'dni', 'recipientDocumentNumber', 'password'];

    // Handle multipart bodies (FormData) by encrypting only matching string fields
    const body = req.body as any;
    if (body instanceof FormData) {
        const clonedFormData = encryptSensitiveFieldsInFormData(body, rsaEncryptionService, sensitiveFields);
        return next(req.clone({ body: clonedFormData }));
    }

    // Skip processing for non-JSON binary bodies
    const isNonJsonBody = body instanceof Blob || body instanceof ArrayBuffer;
    if (isNonJsonBody) return next(req);

    let updatedReq = req;

    // Encrypt sensitive fields only for plain objects or arrays
    if (isPlainObject(body) || Array.isArray(body)) {
        const clonedBody = encryptSensitiveFields(body, rsaEncryptionService, sensitiveFields);
        updatedReq = updatedReq.clone({ body: clonedBody });
    }

    return next(updatedReq);
};

// Utility to ensure we only process plain JSON objects
function isPlainObject(value: any): value is Record<string, any> {
    return Object.prototype.toString.call(value) === '[object Object]';
}

// Encrypt only sensitive string fields within FormData; keep files untouched
function encryptSensitiveFieldsInFormData(
    formData: FormData,
    rsaEncryptionService: RSAEncryptionService,
    sensitiveFields: string[]
): FormData {
    const newFormData = new FormData();
    formData.forEach((value, key) => {
        if (typeof value === 'string' && sensitiveFields.includes(key)) {
            const encrypted = rsaEncryptionService.encrypt(value);
            newFormData.append(key, encrypted);
        } else {
            // value is File/Blob or a non-sensitive string
            newFormData.append(key, value as any);
        }
    });
    return newFormData;
}

// Recursive helper function
function encryptSensitiveFields(
    body: any,
    rsaEncryptionService: RSAEncryptionService,
    sensitiveFields: string[]
): any {
    if (Array.isArray(body)) {
        return body.map(item => encryptSensitiveFields(item, rsaEncryptionService, sensitiveFields));
    }

    if (!isPlainObject(body)) {
        return body;
    }

    const newBody: any = {};

    for (const key of Object.keys(body)) {
        const value = body[key];

        if (value === null || value === undefined) {
            newBody[key] = value;
            continue;
        }

        if (sensitiveFields.includes(key) && typeof value === 'string') {
            newBody[key] = rsaEncryptionService.encrypt(value);
            continue;
        }

        if (typeof value === 'object') {
            if (
                value instanceof FormData ||
                value instanceof Blob ||
                value instanceof ArrayBuffer
            ) {
                newBody[key] = value;
            } else {
                newBody[key] = encryptSensitiveFields(value, rsaEncryptionService, sensitiveFields);
            }
            continue;
        }

        newBody[key] = value;
    }

    return newBody;
}
